/**
 * Heuristic matching of library patterns against the current project.
 *
 * Score formula (rough, intentionally simple):
 *   stack(2) + libOverlap(min(libs, 3)) + recency(0..1)
 *
 * The point isn't to be clever — it's to surface patterns the user actually
 * wrote in similar projects, sorted in a sensible order.
 */
import type { ProjectModel } from "../types.js";
import { collectDependencies, listLibrary, type LibraryEntry } from "./library.js";

export interface MatchResult {
  entry: LibraryEntry;
  score: number;
  reasons: string[];
}

const MAX_LIB_BONUS = 3;
const RECENCY_WINDOW_DAYS = 30;

/** Compute a score for a single library entry against the current project. */
export function scoreEntry(entry: LibraryEntry, project: ProjectModel): MatchResult {
  const reasons: string[] = [];
  let score = 0;

  // Stack match (manifest type) — strong signal
  const projectStack = project.rootManifest?.type ?? null;
  if (projectStack && entry.stack === projectStack) {
    score += 2;
    reasons.push(`same stack (${projectStack})`);
  }

  // Dependency overlap — concrete signal that this is a similar codebase
  const projectDeps = new Set(collectDependencies(project));
  const overlap = entry.dependencies.filter((d) => projectDeps.has(d));
  if (overlap.length > 0) {
    const bonus = Math.min(overlap.length, MAX_LIB_BONUS);
    score += bonus;
    const sample = overlap.slice(0, 3).join(", ");
    reasons.push(`shared deps: ${sample}${overlap.length > 3 ? `, +${overlap.length - 3}` : ""}`);
  }

  // Recency — favor recently shared patterns slightly
  const ageDays = (Date.now() - new Date(entry.createdAt).getTime()) / (1000 * 60 * 60 * 24);
  if (ageDays < RECENCY_WINDOW_DAYS) {
    const recencyBonus = (RECENCY_WINDOW_DAYS - ageDays) / RECENCY_WINDOW_DAYS;
    score += recencyBonus;
    if (recencyBonus > 0.5) reasons.push(`recent (${Math.round(ageDays)}d ago)`);
  }

  return { entry, score: Math.round(score * 10) / 10, reasons };
}

/**
 * Return all library patterns matching the current project, ranked by score.
 * Filters out entries with zero score (no stack match, no dep overlap, not recent).
 */
export function findMatching(project: ProjectModel): MatchResult[] {
  return listLibrary()
    .map((entry) => scoreEntry(entry, project))
    .filter((m) => m.score > 0)
    .sort((a, b) => b.score - a.score);
}
